import { computed, onScopeDispose, readonly, ref, toValue, type ComputedRef, type DeepReadonly, type MaybeRefOrGetter, type Ref } from 'vue'

// types
import type {
  CalendarDropTarget,
  CalendarDropValidation,
  CalendarEvent
} from '@/types/components/calendar'

export type { CalendarDropTarget }

/** Pixels the pointer must travel before a mouse drag begins */
const MOUSE_DRAG_THRESHOLD = 4

/** Milliseconds a touch must be held before a drag begins, leaving normal scrolling intact */
const TOUCH_LONG_PRESS_MS = 250

/** Pixels a touch may drift during the long press before it is treated as a scroll */
const TOUCH_SCROLL_CANCEL_THRESHOLD = 10

/** Distance from a scroll container's edge that triggers auto-scrolling */
const AUTO_SCROLL_EDGE_PX = 56

/** Fastest auto-scroll speed, in pixels per frame */
const AUTO_SCROLL_MAX_SPEED = 14

/**
 * Live state of an in-progress drag, consumed by the floating ghost chip.
 */
export interface CalendarDragState {
  /** Event being moved */
  event: CalendarEvent
  /** Pointer that started the drag */
  pointerId: number
  /** 'mouse', 'touch', or 'pen' */
  pointerType: string
  /** Whether the drag has passed its activation threshold */
  active: boolean
  /** Where the pointer first went down, used to measure travel */
  originX: number
  originY: number
  /** Current pointer position */
  pointerX: number
  pointerY: number
  /** Where inside the chip the pointer grabbed, so the ghost tracks naturally */
  grabOffsetX: number
  grabOffsetY: number
  /** Size of the original chip, copied onto the ghost */
  width: number
  height: number
  /** Location currently beneath the pointer */
  target: CalendarDropTarget | null
  /** Whether dropping here is permitted */
  validation: CalendarDropValidation | null
}

/**
 * Options accepted by useCalendarDragDrop.
 */
export interface UseCalendarDragDropOptions {
  /** Rounding applied to time-grid drops, in minutes */
  snapMinutes?: MaybeRefOrGetter<number>
  /** Whether dragging is currently permitted at all */
  enabled?: MaybeRefOrGetter<boolean>
  /** Decides whether an individual event may be dragged */
  canDrag: (event: CalendarEvent) => boolean
  /** Validates a candidate target so the ghost can show a rejection state */
  validate: (event: CalendarEvent, target: CalendarDropTarget) => CalendarDropValidation
  /** Called on release over a valid target */
  onDrop: (event: CalendarEvent, target: CalendarDropTarget) => void
  /** Scrollable element auto-scrolled when the pointer nears its edges */
  scrollContainer?: () => HTMLElement | null | undefined
}

/**
 * What the calendar views need to wire drag-and-drop into their chips.
 */
export interface UseCalendarDragDropReturn {
  /** Live drag state, or null when idle */
  dragState: DeepReadonly<Ref<CalendarDragState | null>>
  /** Whether a drag has passed its activation threshold */
  isDragging: ComputedRef<boolean>
  /** Id of the event being dragged, so views can hide the original chip */
  draggingEventId: ComputedRef<string | null>
  /** Attach to an event chip's pointerdown */
  onPointerDown: (pointerEvent: PointerEvent, event: CalendarEvent) => void
  /** Aborts any in-progress drag */
  cancelDrag: () => void
}

/**
 * Clamps a number into an inclusive range.
 * @param value - Value to clamp
 * @param min - Lower bound
 * @param max - Upper bound
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Finds the drop zone beneath a point and converts it into a target.
 * Zones opt in with `data-calendar-drop`, so both grids share this single code path.
 * @param clientX - Pointer x in viewport coordinates
 * @param clientY - Pointer y, used to pick the zone
 * @param minuteY - Pointer y adjusted for the grab offset, used to pick the time
 * @param snapMinutes - Rounding applied to time-grid drops
 */
function resolveDropTarget(
  clientX: number,
  clientY: number,
  minuteY: number,
  snapMinutes: number
): CalendarDropTarget | null {
  const element = document.elementFromPoint(clientX, clientY)

  // The pointer left the window entirely
  if (!element) {
    return null
  }

  const zone = element.closest<HTMLElement>('[data-calendar-drop]')

  // Not over a drop zone, e.g. the toolbar
  if (!zone?.dataset.date) {
    return null
  }

  const kind = zone.dataset.calendarDrop

  // Month cells and the all-day row only carry a date
  if (kind === 'day' || kind === 'all-day') {
    return { date: zone.dataset.date, minutes: null, allDay: kind === 'all-day' }
  }

  const rect = zone.getBoundingClientRect()

  // A collapsed column has no scale to map the pointer onto
  if (rect.height <= 0) {
    return null
  }

  const startMinute = Number(zone.dataset.startMinute ?? 0)
  const endMinute = Number(zone.dataset.endMinute ?? 1440)
  const ratio = clamp((minuteY - rect.top) / rect.height, 0, 1)
  const rawMinute = startMinute + ratio * (endMinute - startMinute)
  const step = Math.max(1, snapMinutes)

  return {
    date: zone.dataset.date,
    minutes: clamp(Math.round(rawMinute / step) * step, startMinute, endMinute - step),
    allDay: false
  }
}

/**
 * Pointer-driven drag-and-drop for calendar events.
 *
 * Pointer Events give mouse, pen, and touch a single code path. Mouse drags start after a
 * few pixels of movement, while touch drags wait for a short long-press so the calendar can
 * still be scrolled with a finger. Once a touch drag is active, `touchmove` is cancelled to
 * stop the browser scrolling underneath it.
 * @param options - Drag behaviour and callbacks
 */
export function useCalendarDragDrop(options: UseCalendarDragDropOptions): UseCalendarDragDropReturn {
  const dragState = ref<CalendarDragState | null>(null)

  /** Timer that promotes a held touch into a drag */
  let longPressTimer: ReturnType<typeof setTimeout> | null = null

  /** Handle for the auto-scroll animation loop */
  let autoScrollFrame: number | null = null

  /** Signed pixels-per-frame applied by the auto-scroll loop */
  let autoScrollSpeed = 0

  const isDragging = computed(() => dragState.value?.active === true)

  const draggingEventId = computed(() => (dragState.value?.active ? dragState.value.event.id : null))

  /**
   * Stops the auto-scroll loop.
   */
  function stopAutoScroll(): void {
    // Nothing scheduled
    if (autoScrollFrame === null) {
      return
    }

    cancelAnimationFrame(autoScrollFrame)
    autoScrollFrame = null
    autoScrollSpeed = 0
  }

  /**
   * Scrolls the container by the current speed and re-evaluates the drop target.
   */
  function stepAutoScroll(): void {
    const container = options.scrollContainer?.()
    const state = dragState.value

    // The drag ended or the container disappeared between frames
    if (!container || !state?.active || autoScrollSpeed === 0) {
      stopAutoScroll()
      return
    }

    container.scrollTop += autoScrollSpeed
    updateTarget(state.pointerX, state.pointerY)
    autoScrollFrame = requestAnimationFrame(stepAutoScroll)
  }

  /**
   * Starts, adjusts, or stops auto-scrolling based on how close the pointer is to an edge.
   * @param clientY - Current pointer y
   */
  function updateAutoScroll(clientY: number): void {
    const container = options.scrollContainer?.()

    // Nothing to scroll
    if (!container) {
      return
    }

    const rect = container.getBoundingClientRect()
    const distanceFromTop = clientY - rect.top
    const distanceFromBottom = rect.bottom - clientY

    // Near the top edge: scroll up proportionally to how close the pointer is
    if (distanceFromTop < AUTO_SCROLL_EDGE_PX) {
      autoScrollSpeed = -AUTO_SCROLL_MAX_SPEED * (1 - Math.max(distanceFromTop, 0) / AUTO_SCROLL_EDGE_PX)
    } else if (distanceFromBottom < AUTO_SCROLL_EDGE_PX) {
      autoScrollSpeed = AUTO_SCROLL_MAX_SPEED * (1 - Math.max(distanceFromBottom, 0) / AUTO_SCROLL_EDGE_PX)
    } else {
      autoScrollSpeed = 0
    }

    // Stop the loop once the pointer moves back into the middle
    if (autoScrollSpeed === 0) {
      stopAutoScroll()
      return
    }

    // Start the loop on the first frame that needs it
    if (autoScrollFrame === null) {
      autoScrollFrame = requestAnimationFrame(stepAutoScroll)
    }
  }

  /**
   * Recomputes the target beneath the pointer and revalidates it.
   * @param clientX - Pointer x
   * @param clientY - Pointer y
   */
  function updateTarget(clientX: number, clientY: number): void {
    const state = dragState.value

    // Drag ended between the event firing and this call
    if (!state) {
      return
    }

    const snapMinutes = toValue(options.snapMinutes) ?? 15
    const target = resolveDropTarget(clientX, clientY, clientY - state.grabOffsetY, snapMinutes)

    state.target = target
    state.validation = target ? options.validate(state.event, target) : null
  }

  /**
   * Promotes a pending drag into an active one.
   */
  function activateDrag(): void {
    const state = dragState.value

    // The pointer was already released
    if (!state || state.active) {
      return
    }

    state.active = true
    updateTarget(state.pointerX, state.pointerY)

    // A short buzz confirms the long press on touch devices that support it
    if (state.pointerType === 'touch' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(10)
    }
  }

  /**
   * Removes every window-level listener and resets transient drag state.
   */
  function teardown(): void {
    // Clear a long press that never matured into a drag
    if (longPressTimer !== null) {
      clearTimeout(longPressTimer)
      longPressTimer = null
    }

    stopAutoScroll()

    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
    window.removeEventListener('pointercancel', handlePointerCancel)
    window.removeEventListener('touchmove', handleTouchMove)
    window.removeEventListener('contextmenu', handleContextMenu)
    window.removeEventListener('scroll', handlePointerCancel, true)

    dragState.value = null
  }

  /**
   * Tracks the pointer, activating the drag once its threshold is met.
   * @param pointerEvent - Native pointermove
   */
  function handlePointerMove(pointerEvent: PointerEvent): void {
    const state = dragState.value

    // Ignore other pointers, e.g. a second finger
    if (!state || pointerEvent.pointerId !== state.pointerId) {
      return
    }

    state.pointerX = pointerEvent.clientX
    state.pointerY = pointerEvent.clientY

    // Once active, every move updates the target and edge scrolling
    if (state.active) {
      updateTarget(pointerEvent.clientX, pointerEvent.clientY)
      updateAutoScroll(pointerEvent.clientY)
      return
    }

    const travelled = Math.hypot(pointerEvent.clientX - state.originX, pointerEvent.clientY - state.originY)

    // Touch drags wait for the long press; drifting first means the user is scrolling
    if (state.pointerType === 'touch') {
      // Abandon the pending drag so the browser keeps handling the swipe
      if (travelled > TOUCH_SCROLL_CANCEL_THRESHOLD) {
        teardown()
      }

      return
    }

    // Mouse and pen drags begin as soon as the pointer has travelled far enough
    if (travelled >= MOUSE_DRAG_THRESHOLD) {
      activateDrag()
    }
  }

  /**
   * Completes the drag, invoking the drop callback when the target is valid.
   * @param pointerEvent - Native pointerup
   */
  function handlePointerUp(pointerEvent: PointerEvent): void {
    const state = dragState.value

    // Ignore other pointers
    if (!state || pointerEvent.pointerId !== state.pointerId) {
      return
    }

    const { active, event, target, validation } = state

    teardown()

    // A tap that never became a drag falls through to the chip's click handler
    if (!active || !target || validation?.allowed !== true) {
      return
    }

    options.onDrop(event, target)
  }

  /**
   * Aborts the drag when the pointer is cancelled by the browser or the page scrolls.
   */
  function handlePointerCancel(): void {
    teardown()
  }

  /**
   * Suppresses native scrolling once a touch drag is active.
   * Registered as non-passive so preventDefault is honoured.
   * @param touchEvent - Native touchmove
   */
  function handleTouchMove(touchEvent: TouchEvent): void {
    // Only block the page once the drag has actually started
    if (dragState.value?.active && touchEvent.cancelable) {
      touchEvent.preventDefault()
    }
  }

  /**
   * Suppresses the long-press context menu while dragging on touch.
   * @param menuEvent - Native contextmenu
   */
  function handleContextMenu(menuEvent: Event): void {
    // Only intercept while a drag is genuinely under way
    if (dragState.value) {
      menuEvent.preventDefault()
    }
  }

  /**
   * Begins tracking a potential drag from an event chip.
   * @param pointerEvent - Native pointerdown from the chip
   * @param event - Event the chip represents
   */
  function onPointerDown(pointerEvent: PointerEvent, event: CalendarEvent): void {
    // Dragging is switched off for this calendar
    if (toValue(options.enabled) === false) {
      return
    }

    // Secondary mouse buttons should open menus, not drag
    if (pointerEvent.pointerType === 'mouse' && pointerEvent.button !== 0) {
      return
    }

    // This particular event is locked
    if (!options.canDrag(event)) {
      return
    }

    // A previous drag never cleaned up
    if (dragState.value) {
      teardown()
    }

    const chip = (pointerEvent.currentTarget as HTMLElement | null) ?? (pointerEvent.target as HTMLElement)
    const rect = chip.getBoundingClientRect()

    dragState.value = {
      event,
      pointerId: pointerEvent.pointerId,
      pointerType: pointerEvent.pointerType,
      active: false,
      originX: pointerEvent.clientX,
      originY: pointerEvent.clientY,
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetX: pointerEvent.clientX - rect.left,
      grabOffsetY: pointerEvent.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      target: null,
      validation: null
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerCancel)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('contextmenu', handleContextMenu)

    // Touch drags wait for a hold so a swipe still scrolls the calendar
    if (pointerEvent.pointerType === 'touch') {
      longPressTimer = setTimeout(activateDrag, TOUCH_LONG_PRESS_MS)
      return
    }

    // Mouse and pen start immediately on movement, so block text selection now
    pointerEvent.preventDefault()
  }

  /**
   * Aborts any in-progress drag.
   */
  function cancelDrag(): void {
    teardown()
  }

  onScopeDispose(() => {
    teardown()
  })

  return {
    dragState: readonly(dragState),
    isDragging,
    draggingEventId,
    onPointerDown,
    cancelDrag
  }
}
